import { type RefObject, useEffect } from "react";
import {
  useCenterTopologyItemId,
  useComputedTopologyLayout,
} from "stores/wizardStore";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import type { InteractiveNode } from "interfaces/product/topology/interactiveLayer.types";

type UseCenterTopologyItemProps = {
  viewportRef: RefObject<HTMLElement>;
  transformRef: RefObject<ReactZoomPanPinchRef>;
};

export function useCenterTopologyItem({
  transformRef,
  viewportRef,
}: UseCenterTopologyItemProps) {
  const centerTopologyItemId = useCenterTopologyItemId();
  const layout = useComputedTopologyLayout();

  useEffect(() => {
    if (
      !centerTopologyItemId ||
      !layout?.nodes?.length ||
      !transformRef.current ||
      !viewportRef.current
    ) {
      return;
    }

    const node = layout.nodes.find(
      (n: InteractiveNode) => n.id === centerTopologyItemId,
    );

    if (!node) {
      return;
    }

    const { instance } = transformRef.current;
    if (!instance) {
      return;
    }

    const { scale } = instance.transformState;
    const viewport = viewportRef.current.getBoundingClientRect();

    const positionX =
      -node.x * scale + viewport.width / 2 - viewport.width * 0.125 - 120;
    const positionY =
      -node.y * scale + viewport.height / 2 - viewport.height * 0.125 - 120;

    requestAnimationFrame(() => {
      transformRef.current.setTransform(positionX, positionY, scale, 300);
    });
  }, [centerTopologyItemId, layout, transformRef, viewportRef]);
}
